// The device identity: Ed25519 + X25519, generated non-extractable and kept as CryptoKey objects
// in IndexedDB (docs/protocol.md, "Identities and keys"). The private keys can never leave the phone.
import { generateIdentity, idFor, publicIdentity, type Identity, type PublicIdentity } from "@whistle/protocol";
import { dbGet, dbPut, dbDelete } from "./db";

export type DeviceIdentity = {
  identity: Identity;
  pub: PublicIdentity;
  deviceId: string;
  createdAt: number;
};

/** What is stored: the CryptoKey pairs (structured-cloned), plus the public forms for convenience. */
export type StoredDeviceIdentity = { sig: CryptoKeyPair; enc: CryptoKeyPair; pub: PublicIdentity; deviceId: string; createdAt: number };

const KEY = "identity";

export async function loadIdentity(): Promise<DeviceIdentity | null> {
  const s = await dbGet<StoredDeviceIdentity>("kv", KEY);
  if (!s) return null;
  return { identity: { sig: s.sig, enc: s.enc }, pub: s.pub, deviceId: s.deviceId, createdAt: s.createdAt };
}

/** A new identity, in memory only until saveIdentity: a pairing that is cancelled must leave the current one in place. */
export async function generateDeviceIdentity(now = Date.now()): Promise<DeviceIdentity> {
  const identity = await generateIdentity(false);
  const pub = await publicIdentity(identity);
  return { identity, pub, deviceId: await idFor(pub.sigPub), createdAt: now };
}

export async function saveIdentity(d: DeviceIdentity): Promise<void> {
  const stored: StoredDeviceIdentity = { sig: d.identity.sig, enc: d.identity.enc, pub: d.pub, deviceId: d.deviceId, createdAt: d.createdAt };
  await dbPut("kv", stored, KEY);
}

export async function forgetIdentity(): Promise<void> {
  await dbDelete("kv", KEY);
}

/** True when this browser can do what the app needs. Checked before any keys are made (SPEC 7.1 step 5). */
export async function checkCryptoSupport(): Promise<{ ok: true } | { ok: false; reason: string }> {
  if (typeof indexedDB === "undefined") return { ok: false, reason: "This browser has no storage for the app's keys. A private tab or an unsupported browser can cause this." };
  if (!globalThis.crypto?.subtle) return { ok: false, reason: "This browser cannot make the keys the app needs. Open the link in Chrome or Samsung Internet." };
  try {
    await crypto.subtle.generateKey({ name: "Ed25519" }, false, ["sign", "verify"]);
    await crypto.subtle.generateKey({ name: "X25519" }, false, ["deriveBits"]);
  } catch {
    return { ok: false, reason: "This browser is too old for the app's encryption. Chrome 137 or later, or a recent Samsung Internet, is needed." };
  }
  return { ok: true };
}
